import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIsomorphicLayoutEffect } from './hooks/useIsomorphicLayoutEffect';
import { useWindowWidth } from './hooks/useWindowWidth';
import {
  circlesSection,
  doorTeasers,
  fullReleaseWaitlist,
  finalCta,
  marketingAnchors,
  type MarketingDoorId,
  marketingCta,
  marketingFooter,
  marketingNav,
  beyondTheCircleSection,
  howItWorksSection,
  pricingHonestySection,
  problemSection,
  openMindedCirclesSection,
  signalFireSection,
  socialSettingsSection,
  meetupSection,
  testimonialsSection,
  marketingFaq,
  faqSection,
  whyItWorksSection,
  weCare,
} from '../../app/_shared/marketing/landingContent';
import { EmberBackground } from './components/ember/EmberBackground';
import { BeyondTheCircleSection } from './components/marketing/BeyondTheCircleSection';
import { CirclesExplanationSection } from './components/marketing/CirclesExplanationSection';
import { HowItWorksBand } from './components/marketing/HowItWorksBand';
import { FinalCtaBand } from './components/marketing/FinalCtaBand';
import { WaitlistPremiumSection } from './components/marketing/WaitlistPremiumSection';
import { CircleScreenshotsRow } from './components/marketing/CircleScreenshotsRow';
import { MarketingFooterColumns } from './components/marketing/MarketingFooterColumns';
import { OpenMindedCirclesSection } from './components/marketing/OpenMindedCirclesSection';
import { PricingHonestySection } from './components/marketing/PricingHonestySection';
import { ShareBand } from './components/marketing/ShareBand';
import { ProblemSection } from './components/marketing/ProblemSection';
import { SignalFireSection } from './components/marketing/SignalFireSection';
import { SocialSettingsSection } from './components/marketing/SocialSettingsSection';
import { SafetyTrustPanel } from './components/marketing/SafetyTrustPanel';
import { StoreBadgeRow } from './components/marketing/StoreBadgeRow';
import { TestimonialsSection } from './components/marketing/TestimonialsSection';
import { FaqSection } from './components/marketing/FaqSection';
import { WhyItWorksSection } from './components/marketing/WhyItWorksSection';
import { MeetupSection } from './components/marketing/MeetupSection';
import { MobileStickyCtaBar } from './components/marketing/MobileStickyCtaBar';
import { KynwardLogo } from './components/brand/KynwardLogo';
import { PresenceHero } from './presence/PresenceHero';
import { Reveal } from './Reveal';
import { useSmartStoreHref } from './hooks/useSmartStoreHref';
import {
  resumeWalkthroughPinLock,
  suspendWalkthroughPinLock,
} from './hooks/useWalkthroughSectionStage';
import { useMarketingPresence } from './presence/useMarketingPresence';
import { isExternalStoreUrl } from './utils/storePlatform';
import { marketingAppDownloadVisible, links } from './config';
import { markWaitlistSignupSource } from './waitlist/waitlistSignupSource';
import { setWaitlistDoor } from './waitlist/fullReleaseWaitlist';
import './MarketingPage.css';
import './components/marketing/marketingProduct.css';
import './components/marketing/landingRef.css';
import './components/marketing/marketing-responsive.css';

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  // The Circle walkthrough's pin caps wheel/touch scroll speed while engaged (see
  // useWalkthroughPinScrollLock) — that clamp otherwise fights a smooth-scroll jump that only
  // passes *through* the pin zone, stalling it before it reaches a target further down the page.
  suspendWalkthroughPinLock();
  let settled = false;
  const resume = () => {
    if (settled) return;
    settled = true;
    resumeWalkthroughPinLock();
    window.removeEventListener('scrollend', resume);
  };
  window.addEventListener('scrollend', resume, { once: true });
  // Fallback in case `scrollend` never fires (target already in view, or unsupported).
  window.setTimeout(resume, 1200);
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function scrollToWaitlist(source: string) {
  markWaitlistSignupSource(source);
  scrollToId(marketingAnchors.waitlist);
}

/** ?door=him|her|open — campaign-link variant of the /for-him, /for-her, /open-minded routes. */
function readDoorParam(): MarketingDoorId | null {
  if (typeof window === 'undefined') return null;
  const d = new URLSearchParams(window.location.search).get('door')?.trim().toLowerCase();
  return d === 'him' || d === 'her' || d === 'open' ? d : null;
}

/** The CSS mobile breakpoint, in one place. See `@media (max-width: 900px)` across the marketing stylesheets. */
const MOBILE_MAX_WIDTH = 900;

export function MarketingPage({ door }: { door?: MarketingDoorId } = {}) {
  const w = useWindowWidth();
  // Route prop wins over the query param; read once — a mid-session param change is not a thing.
  // Read after mount rather than in the initializer: the prerendered HTML carries no query string,
  // so seeding straight from `?door=` would make the first client render disagree with the shipped
  // markup and cost the whole prerendered tree. The layout effect lands the door before paint.
  const [urlDoor, setUrlDoor] = useState<MarketingDoorId | null>(null);
  useIsomorphicLayoutEffect(() => {
    setUrlDoor(readDoorParam());
  }, []);
  const resolvedDoor = door ?? urlDoor;
  const doorTeaser = resolvedDoor ? doorTeasers[resolvedDoor] : null;
  const doorWaitlistSource = useCallback(
    (base: string) => (resolvedDoor ? `${base}-${resolvedDoor}` : base),
    [resolvedDoor]
  );
  // The waitlist metadata reads the door via this singleton (route-prop precedence must reach it;
  // re-deriving from the URL there would miss /for-him and disagree on mismatched links).
  useEffect(() => {
    setWaitlistDoor(resolvedDoor);
  }, [resolvedDoor]);
  const wideHero = w >= 1024;
  // Mirrors the CSS mobile breakpoint. This decides whether the nav links are *marked* hideable;
  // the hiding itself is `@media (max-width: 900px)` in MarketingPage.css. They drifted apart once
  // already — when the CSS boundary moved to 900 and this did not, tablets got a desktop nav of
  // 21px targets sitting on top of a mobile page.
  const narrowNav = w <= MOBILE_MAX_WIDTH;
  const compactNavLabels = w < 1080;
  const showAppDownload = marketingAppDownloadVisible();
  const navCtaLabel = w < 400 ? 'Download' : marketingCta.downloadApp;
  const navWaitlistLabel =
    w < 400 ? marketingCta.joinWaitlistShort : marketingCta.joinFullReleaseList;
  const [navScrolled, setNavScrolled] = useState(false);

  const {
    variant,
    hero,
    activityLine,
    motionKilled,
    disableAsideFloat,
    isLoading,
    onLandingView,
    onHeroInView,
    onPrimaryCtaClick,
    onSecondaryCtaClick,
  } = useMarketingPresence({ door: resolvedDoor });

  useEffect(() => {
    onLandingView();
  }, [onLandingView]);

  useEffect(() => {
    const onScroll = () => setNavScrolled(typeof window !== 'undefined' && window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const downloadHref = `#${marketingAnchors.download}`;
  const navDownloadHref = useSmartStoreHref(downloadHref);
  const navOpensStore = isExternalStoreUrl(navDownloadHref);

  const heroForPage = useMemo(() => {
    if (!showAppDownload) {
      return {
        ...hero,
        primaryCta: {
          label: marketingCta.joinFullReleaseList,
          href: `#${marketingAnchors.waitlist}`,
        },
      };
    }
    if (!navOpensStore) return hero;
    return {
      ...hero,
      primaryCta: { ...hero.primaryCta, href: navDownloadHref },
    };
  }, [hero, navDownloadHref, navOpensStore, showAppDownload]);

  // No waitlist-source mark here: in waitlist mode the brandScene hero renders the inline email
  // capture instead of this CTA (door attribution rides that capture's own source string), and in
  // download mode there is nothing to mark.
  const handlePrimaryCtaClick = useCallback(() => {
    onPrimaryCtaClick();
  }, [onPrimaryCtaClick]);

  const scrollToFooterAnchor = useCallback(
    (id: string) => {
      if (id === marketingAnchors.waitlist) {
        scrollToWaitlist(doorWaitlistSource('landing-footer'));
        return;
      }
      scrollToId(id);
    },
    [doorWaitlistSource]
  );

  return (
    <EmberBackground>
      <div className="ml ml-landingPremium" data-testid="marketing-landing">
        <header className={`ml-nav nav${navScrolled ? ' ml-nav--scrolled' : ''}`}>
          <div className="ml-nav__inner nav__inner">
            <a className="ml-nav__wordmark" href="/">
              <KynwardLogo size={28} glow="subtle" className="ml-nav__logo" />
              <span className="ml-nav__wordmarkText">Kynward</span>
            </a>
            <div className={`ml-nav__links ${narrowNav ? 'ml-nav__links--hideMobile' : ''}`}>
              {/* Shown only at the wide breakpoint. The compact range (768–1080px) already fits
                  four labels against the wordmark and CTA; a fifth is the one that overflows. */}
              {compactNavLabels ? null : (
                <button
                  type="button"
                  className="ml-nav__link"
                  onClick={() => scrollToId(marketingAnchors.whyItWorks)}>
                  {marketingNav.whyItWorks}
                </button>
              )}
              <button
                type="button"
                className="ml-nav__link"
                onClick={() => scrollToId(marketingAnchors.how)}>
                {marketingNav.theFlow}
              </button>
              <button
                type="button"
                className="ml-nav__link"
                onClick={() => scrollToId(marketingAnchors.meetups)}>
                {marketingNav.meetups}
              </button>
              <button
                type="button"
                className="ml-nav__link"
                onClick={() => scrollToId(marketingAnchors.safety)}>
                {marketingNav.safety}
              </button>
              <button
                type="button"
                className="ml-nav__link"
                onClick={() => scrollToId(marketingAnchors.faq)}>
                {marketingNav.questions}
              </button>
            </div>
            {showAppDownload ? (
              <a
                className="ml-nav__cta"
                href={navDownloadHref}
                {...(navOpensStore ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                {navCtaLabel}
              </a>
            ) : (
              <button
                type="button"
                className="ml-nav__cta"
                onClick={() => scrollToWaitlist(doorWaitlistSource('landing-nav'))}>
                {navWaitlistLabel}
              </button>
            )}
          </div>
        </header>

        <main>
          <section
            id={!showAppDownload ? marketingAnchors.waitlist : undefined}
            className="hero-section hero-section--product"
            aria-label="Introduction">
            <div className="hero-scrim" aria-hidden />
            <div className="hero-content">
              <PresenceHero
                wide={wideHero}
                variant={variant}
                hero={heroForPage}
                activityLineOverride={activityLine}
                motionKilled={motionKilled}
                disableAsideFloat={disableAsideFloat}
                isLoading={isLoading}
                brandScene
                door={resolvedDoor ?? undefined}
                showAppDownload={showAppDownload}
                onScrollToAnchor={scrollToId}
                onPrimaryCtaClick={handlePrimaryCtaClick}
                onSecondaryCtaClick={onSecondaryCtaClick}
                onHeroInView={onHeroInView}
              />
            </div>
          </section>

          {doorTeaser ? (
            /* Per-door emphasis — a pointer band, NOT a section reorder: the full sections stay
               put so nothing structural moves near the Circles scroll-pin. */
            <section
              className="ml-section ml-section--washWarm ml-doorTeaser"
              aria-label={doorTeaser.eyebrow}>
              <p className="ml-doorTeaser__eyebrow">{doorTeaser.eyebrow}</p>
              <p className="ml-doorTeaser__line">{doorTeaser.line}</p>
              <button
                type="button"
                className="ml-doorTeaser__cta"
                onClick={() => scrollToId(marketingAnchors[doorTeaser.anchorKey])}>
                {doorTeaser.cta}
              </button>
            </section>
          ) : null}

          <div id={marketingAnchors.problem} className="ml-scrollPad" />
          {/* The problem the rest of the page answers, stated before any mechanics. This copy
              existed as `makingFriendsProse` + `forYouSection` and was imported by nothing, so the
              page opened straight into "Every evening, a Circle forms near you" and never said what
              it was fixing. Sits ABOVE the #how anchor so "See how it works" still lands on the
              Drop, and carries no wash (no ::before) — the walkthrough pin is sensitive to new
              stacking contexts nearby. */}
          <Reveal className="ml-section ml-section--problem" motion="fade" stagger>
            <ProblemSection
              eyebrow={problemSection.eyebrow}
              h2={problemSection.h2}
              p1Before={problemSection.p1Before}
              p1Em={problemSection.p1Em}
              p1After={problemSection.p1After}
              pains={problemSection.pains}
              bridgeLead={problemSection.bridgeLead}
              bridge={problemSection.bridge}
              forYouEyebrow={problemSection.forYouEyebrow}
              forYou={problemSection.forYou}
            />
          </Reveal>

          <hr className="ml-section-divider" aria-hidden />

          {/* The target for "See how it works" (hero), "How it works" (nav and footer). All three
              pointed at #how and nothing rendered that id, so `scrollToId` fetched null and returned
              — a button that ran its handler and moved nothing. The flow starts here: pick a night,
              then what a Circle is. `#circles` stays where it is, further down, because the nav
              offers both and they should not land in the same place. */}
          <div id={marketingAnchors.how} className="ml-scrollPad" />
          <div className="ml-product-flow">
            <div className="ml-product-flow__shell">
              <Reveal
                className="ml-section ml-section--howWorks ml-section--elevated ml-section--washWarm"
                motion="blurIn"
                once>
                <HowItWorksBand {...howItWorksSection} />
              </Reveal>

              <hr className="ml-section-divider" aria-hidden />

              <div id={marketingAnchors.circles} />
              <div className="ml-section ml-section--circles">
                <CirclesExplanationSection
                  h2={circlesSection.h2}
                  subtitle={circlesSection.subtitle}
                  timeline={circlesSection.timeline}
                  previews={circlesSection.previews}
                  featureChips={circlesSection.featureChips}
                />
              </div>
              <Reveal
                className="ml-section ml-section--circleShots ml-section--washCool"
                motion="fade"
                fadeOnly>
                <CircleScreenshotsRow groups={circlesSection.shotGroups} />
              </Reveal>
            </div>
          </div>

          {/* Who a Circle is for, answered right after what a Circle is — the band carries its own
              full-bleed border-top, so no divider here. */}
          <div id={marketingAnchors.whyItWorks} className="ml-scrollPad" />
          {/* Why each mechanic works, immediately after the walkthrough that shows them. The page
              had "how" everywhere and "why" nowhere. No wash here either — this is the first block
              after the pin zone. */}
          <hr className="ml-section-divider" aria-hidden />
          <Reveal className="ml-section ml-section--whyWorks" motion="fade" stagger>
            <WhyItWorksSection
              eyebrow={whyItWorksSection.eyebrow}
              h2={whyItWorksSection.h2}
              lead={whyItWorksSection.lead}
              items={whyItWorksSection.items}
              inputs={whyItWorksSection.inputs}
              closer={whyItWorksSection.closer}
            />
          </Reveal>

          {/* The meet-up beat, directly after the walkthrough and the why. It sat ninth of thirteen
              sections, below both secondary lanes and the Settings roster, and the one thing every
              current survey of this audience agrees on is that they want to end up in person — so
              the section that says "a match is not a coffee" should not be the second-to-last
              product section a reader reaches. Safe to move here: WhyItWorks is already the first
              block after the walkthrough's scroll-pin, and this one carries its own Reveal wash. */}
          <div id={marketingAnchors.meetups} className="ml-scrollPad" />
          <hr className="ml-section-divider" aria-hidden />
          <Reveal
            className="ml-section ml-section--meetups ml-section--washWarm"
            motion="fade"
            stagger>
            <MeetupSection
              eyebrow={meetupSection.eyebrow}
              h2={meetupSection.h2}
              lead={meetupSection.lead}
              items={meetupSection.items}
              venueChips={meetupSection.venueChips}
              safetyRowLabel={meetupSection.safetyRowLabel}
              safetyNote={meetupSection.safetyNote}
              closer={meetupSection.closer}
            />
          </Reveal>

          <div id={marketingAnchors.openMinded} className="ml-scrollPad" />
          <Reveal className="ml-section ml-section--openMinded" motion="slideRight">
            <OpenMindedCirclesSection
              h2={openMindedCirclesSection.h2}
              lead={openMindedCirclesSection.lead}
              paragraphs={openMindedCirclesSection.paragraphs}
              pillars={openMindedCirclesSection.pillars}
            />
          </Reveal>

          <hr className="ml-section-divider" aria-hidden />
          <Reveal className="ml-section ml-section--settings" motion="blurIn" once>
            <SocialSettingsSection content={socialSettingsSection} />
          </Reveal>

          {/* One section, two lanes. Signal Fire used to be its own band above this one, which put
              the lane outside the heading that describes it; the Mutuals panel that used to sit
              here is gone, because the Circle walkthrough already ends on that beat. The signalFire
              anchor stays — the footer links to it — and lands on the lane inside the group. */}
          <div id={marketingAnchors.beyondCircle} className="ml-scrollPad" />
          <hr className="ml-section-divider" aria-hidden />
          <Reveal
            className="ml-section ml-section--beyondCircle ml-section--washCool"
            motion="fade"
            stagger>
            <BeyondTheCircleSection
              title={beyondTheCircleSection.title}
              intro={beyondTheCircleSection.intro}>
              <div id={marketingAnchors.signalFire} className="ml-scrollPad" />
              <SignalFireSection
                eyebrow={signalFireSection.eyebrow}
                h2={signalFireSection.h2}
                lead={signalFireSection.lead}
                steps={signalFireSection.steps}
                footnote={signalFireSection.footnote}
                chips={signalFireSection.chips}
              />
            </BeyondTheCircleSection>
          </Reveal>

          <div id={marketingAnchors.different} className="ml-scrollPad" />
          <hr className="ml-section-divider" aria-hidden />
          <Reveal
            className="ml-section ml-section--whyDifferent ml-section--washCool"
            motion="fade"
            stagger>
            <TestimonialsSection
              h2={testimonialsSection.h2}
              lead={testimonialsSection.lead}
              items={testimonialsSection.items}
            />
          </Reveal>

          <div id={marketingAnchors.pricing} className="ml-scrollPad" />
          <hr className="ml-section-divider" aria-hidden />
          <Reveal className="ml-section ml-section--pricing" motion="fade" stagger>
            <PricingHonestySection
              eyebrow={pricingHonestySection.eyebrow}
              h2={pricingHonestySection.h2}
              lead={pricingHonestySection.lead}
              items={pricingHonestySection.items}
              closer={pricingHonestySection.closer}
            />
          </Reveal>

          <div id={marketingAnchors.safety} className="ml-scrollPad" />
          <hr className="ml-section-divider" aria-hidden />
          <Reveal className="ml-section ml-section--safety ml-section--washWarm" motion="zoom">
            <SafetyTrustPanel
              h2={weCare.h2}
              body={weCare.body}
              learnMoreLabel={weCare.learnMoreLabel}
              learnMoreHref={weCare.learnMoreHref}
              checklist={weCare.checklist}
            />
          </Reveal>

          <div id={marketingAnchors.faq} className="ml-scrollPad" />
          <hr className="ml-section-divider" aria-hidden />
          <Reveal className="ml-section ml-section--faq ml-section--washCool" motion="fade">
            <FaqSection h2={faqSection.h2} intro={faqSection.intro} items={marketingFaq} />
          </Reveal>

          <div
            id={showAppDownload ? marketingAnchors.waitlist : undefined}
            className="ml-scrollPad"
          />
          <Reveal
            className="ml-section ml-section--waitlist ml-section--waitlistPremium"
            motion="zoom">
            <WaitlistPremiumSection
              h2={fullReleaseWaitlist.h2}
              sub={fullReleaseWaitlist.sub}
              badge={fullReleaseWaitlist.badge}
              inviteLeadIn={marketingCta.inviteLeadIn}
              inviteLinkLabel={marketingCta.inviteEnterCode}
              inviteHref={links.signup}
            />
          </Reveal>

          {/* Placed after the waitlist so it catches the reader at the one moment they have either
              just committed or just decided not to \u2014 both are people who might still pass it on. */}
          <hr className="ml-section-divider" aria-hidden />
          <Reveal className="ml-section ml-section--share" motion="fade">
            <ShareBand />
          </Reveal>

          <div id={marketingAnchors.download} className="ml-scrollPad" />
          <Reveal className="ml-section ml-section--finalCta" motion="riseTall">
            <FinalCtaBand h2={finalCta.h2} sub={finalCta.sub} storeBadges={<StoreBadgeRow />} />
          </Reveal>
        </main>

        {!showAppDownload ? <MobileStickyCtaBar door={resolvedDoor ?? undefined} /> : null}

        <Reveal as="footer" className="ml-footer ml-footer--columns" motion="fade">
          <div className="ml-footer__inner ml-footer__inner--wide page-wrap">
            <MarketingFooterColumns
              tag={marketingFooter.tag}
              columns={marketingFooter.columns}
              onScrollToAnchor={scrollToFooterAnchor}
              year={new Date().getFullYear()}
            />
          </div>
        </Reveal>
      </div>
    </EmberBackground>
  );
}
